import { Locales } from 'shared/assets/localization';
import { NLP } from 'shared/constants';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { Goal, NewGoalWithID } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { ParsedProperties, Person } from 'shared/types/nlp';
import { NewTagWithID, Tag } from 'shared/types/tag';
import { TaskPriorityOptions } from 'shared/types/task-base';
import { isNumber } from 'shared/utils/is-number';
import { getAllDatePatterns } from 'shared/utils/nlp/date-processing';
import {
  getTargetPatternContent,
  parseTarget,
} from 'shared/utils/nlp/frequency-processing';
import { getDisplayDate } from 'shared/utils/nlp/get-display-date';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';
import { getWordIndexFromMatch } from 'shared/utils/nlp/get-word-index-for-match';
import {
  getRrulePatternContent,
  parseRrule,
} from 'shared/utils/nlp/rrule-processing';
import { parseTime } from 'shared/utils/nlp/time-processing';

type PropertiesFromStringOptions = {
  language: Locales;
  dateFormat: DateFormatOptions;
  ignoredWordIds: ID[];
  wordIds: WordId[];
  goals: (Goal | NewGoalWithID)[];
  lifeAreas: LifeArea[];
  tags: (Tag | NewTagWithID)[];
  people: Person[] | undefined;
  createHabitAllowed: boolean;
  scheduleTargetAllowed: boolean;
};

export const processTaskPropertiesFromString = (
  textToProcess: string,
  {
    language,
    dateFormat,
    ignoredWordIds,
    wordIds,
    goals,
    lifeAreas,
    tags,
    people,
    createHabitAllowed,
    scheduleTargetAllowed,
  }: PropertiesFromStringOptions,
): ParsedProperties => {
  const properties: ParsedProperties = {};

  const isIgnored = (match: RegExpMatchArray) => {
    const wordIndex = getWordIndexFromMatch(match, wordIds);
    const wordId = wordIds[wordIndex]?.id;
    return wordId ? ignoredWordIds.includes(wordId) : false;
  };

  // create a linked-item.name -> id map for fast lookup
  const goalNameToIdsMap = new Map<string, ID[]>();
  for (const goal of goals) {
    if (!goalNameToIdsMap.has(goal.name)) {
      goalNameToIdsMap.set(goal.name, []);
    }
    goalNameToIdsMap.get(goal.name)!.push(goal.id);
  }
  const lifeAreaNameToIdsMap = new Map<string, ID[]>();
  for (const lifeArea of lifeAreas) {
    if (!lifeAreaNameToIdsMap.has(lifeArea.name)) {
      lifeAreaNameToIdsMap.set(lifeArea.name, []);
    }
    lifeAreaNameToIdsMap.get(lifeArea.name)!.push(lifeArea.id);
  }

  // Use generic regex to find all potential goals to be used for exclusion.
  const genericLinkedItemRegex = new RegExp(`(~${NLP.goalSubPattern})`, 'gu');
  const allLinkedItemMatches = [
    ...textToProcess.matchAll(genericLinkedItemRegex),
  ].filter((m) => !isIgnored(m));

  // The last goal found is considered the "active" one for the task properties.
  const lastActiveLinkedItemMatch = allLinkedItemMatches.length
    ? allLinkedItemMatches[allLinkedItemMatches.length - 1]
    : undefined;
  if (lastActiveLinkedItemMatch) {
    const linkedItemName = lastActiveLinkedItemMatch[1].substring(1).trim();
    const goalId = goalNameToIdsMap.get(linkedItemName)?.[0] ?? null;
    const lifeAreaId = lifeAreaNameToIdsMap.get(linkedItemName)?.[0] ?? null;
    properties.goal = goalId;
    properties.lifeArea = goalId ? null : lifeAreaId;
  }

  // Helper function to check if a potential property match is inside ANY goal-like string.
  const isInsideAGoal = (match: RegExpMatchArray) => {
    if (!match) {
      return false;
    }
    return allLinkedItemMatches.some(
      (goalMatch) =>
        (match.index ?? -1) >= (goalMatch.index ?? -1) &&
        (match.index ?? -1) + match[0].length <=
          (goalMatch.index ?? -1) + goalMatch[0].length,
    );
  };

  // create a tag-name -> id map for fast lookup
  const tagNameToIdsMap = new Map<string, ID[]>();
  for (const tag of tags) {
    if (!tagNameToIdsMap.has(tag.name)) {
      tagNameToIdsMap.set(tag.name, []);
    }
    tagNameToIdsMap.get(tag.name)!.push(tag.id);
  }

  const tagRegex = new RegExp(`(#${NLP.tagSubPattern})`, 'gu');
  const allTagMatches = [...textToProcess.matchAll(tagRegex)].filter(
    (m) => !isIgnored(m) && !isInsideAGoal(m),
  );
  if (allTagMatches.length) {
    properties.tags = allTagMatches
      .map((match) => {
        const tagName = match[0].substring(1).trim();
        return tagNameToIdsMap.get(tagName)?.[0];
      })
      .filter(Boolean) as ID[];
  }

  const isInsideATag = (match: RegExpMatchArray) => {
    if (!match) {
      return false;
    }
    return allTagMatches.some(
      (tagMatch) =>
        (match.index ?? -1) > (tagMatch.index ?? -1) &&
        (match.index ?? -1) + match[0].length <=
          (tagMatch.index ?? -1) + tagMatch[0].length,
    );
  };

  // create a person-name -> id map for fast lookup
  const persons = people ?? [];
  const personNameToIdsMap = new Map<string, ID[]>();
  for (const person of persons) {
    if (!personNameToIdsMap.has(person.name)) {
      personNameToIdsMap.set(person.name, []);
    }
    personNameToIdsMap.get(person.name)!.push(person.id);
  }

  const personRegex = new RegExp(`(?<!~)@(${NLP.personSubPattern})`, 'gu');
  const allPersonMatches = [...textToProcess.matchAll(personRegex)].filter(
    (m) => !isIgnored(m) && !isInsideAGoal(m) && !isInsideATag(m),
  );
  if (allPersonMatches.length) {
    properties.assignees = allPersonMatches
      .map((match) => {
        const personName = match[1].trim();
        return personNameToIdsMap.get(personName)?.[0];
      })
      .filter(Boolean) as ID[];
  }

  const allDatePatterns = getAllDatePatterns(language);
  // 1. Find all potential schedule candidates FIRST.
  const deadlineRegex = new RegExp(`{(${allDatePatterns})}`, 'gi');

  // Find the *last* valid match for each property type (recurrence, target, deadline, etc.).
  const rrulePatternContent = getRrulePatternContent(language);
  const rruleRegex = new RegExp(`(${rrulePatternContent})`, 'gi');
  const targetPatternContent = getTargetPatternContent(language);
  const targetRegex = new RegExp(`\\b${targetPatternContent}\\b`, 'gi');

  const deadlineCandidates = [...textToProcess.matchAll(deadlineRegex)].filter(
    (m) =>
      !isIgnored(m) &&
      !isInsideAGoal(m) &&
      !isInsideATag(m) &&
      getDisplayDate(m[1], language, dateFormat, true),
  );
  const rruleCandidates = createHabitAllowed
    ? [...textToProcess.matchAll(rruleRegex)].filter(
        (m) =>
          !isIgnored(m) &&
          !isInsideAGoal(m) &&
          !isInsideATag(m) &&
          parseRrule(m[0], language),
      )
    : [];
  const targetCandidates =
    createHabitAllowed && scheduleTargetAllowed
      ? [...textToProcess.matchAll(targetRegex)].filter(
          (m) =>
            !isIgnored(m) &&
            !isInsideAGoal(m) &&
            !isInsideATag(m) &&
            parseTarget(m[0], language),
        )
      : [];

  // 2. Determine the single "winning" schedule item (the one that appears last).
  const allScheduleCandidates = [
    deadlineCandidates.pop(),
    rruleCandidates.pop(),
    targetCandidates.pop(),
  ].filter(Boolean) as RegExpMatchArray[]; // Remove nulls

  allScheduleCandidates.sort((a, b) => (a.index ?? -1) - (b.index ?? -1));
  const winningScheduleMatch = allScheduleCandidates.pop();

  // 3. Process the winning schedule item.
  if (winningScheduleMatch) {
    const text = winningScheduleMatch[0];
    // Check if the match text is a valid rule of each type
    const rruleResult = parseRrule(text, language);
    const deadlineResult = getDisplayDate(
      text.slice(1, -1),
      language,
      dateFormat,
      true,
    );
    const targetResult = scheduleTargetAllowed
      ? parseTarget(text, language)
      : null;

    if (text.startsWith('{') && deadlineResult) {
      properties.deadline = deadlineResult.date;
    } else if (rruleResult) {
      properties.rrule = rruleResult.rrule;
    } else if (targetResult) {
      properties.target = targetResult;
    }
  }

  // 4. Helper to check if a potential date is inside the schedule we just found.
  const isInsideWinningSchedule = (match: RegExpMatchArray) => {
    if (!winningScheduleMatch || !match) {
      return false;
    }
    return (
      (match.index ?? -1) >= (winningScheduleMatch.index ?? -1) &&
      (match.index ?? -1) + match[0].length <=
        (winningScheduleMatch.index ?? -1) + winningScheduleMatch[0].length
    );
  };

  // 5. NOW, find the due date, EXCLUDING any match that was part of the winning schedule.
  const dateRegex = new RegExp(
    `(?<![a-zA-Z0-9~#@!{])(${allDatePatterns})(?![a-zA-Z0-9]}])`,
    'gi',
  );
  const lastDateMatch = [...textToProcess.matchAll(dateRegex)]
    .filter((m) => {
      if (
        isIgnored(m) ||
        isInsideAGoal(m) ||
        isInsideATag(m) ||
        isInsideWinningSchedule(m)
      ) {
        return false;
      }
      return getDisplayDate(m[0], language, dateFormat, false);
    })
    .pop();

  if (lastDateMatch) {
    const result = getDisplayDate(
      lastDateMatch[0],
      language,
      dateFormat,
      false,
    );
    if (result) {
      properties.dueDate = result.date;
    }
  }

  // Find the last valid priority.
  const priorityRegex = new RegExp(`\\bp[1-3]\\b|!![1-3]`, 'gi');
  const lastPriorityMatch = [...textToProcess.matchAll(priorityRegex)]
    .filter((m) => !isIgnored(m) && !isInsideAGoal(m) && !isInsideATag(m))
    .pop();

  if (lastPriorityMatch) {
    const pKeyword = lastPriorityMatch[0].toLowerCase();
    if (pKeyword === 'p1' || pKeyword === '!!1') {
      properties.priority = TaskPriorityOptions.High;
    }
    if (pKeyword === 'p2' || pKeyword === '!!2') {
      properties.priority = TaskPriorityOptions.Medium;
    }
    if (pKeyword === 'p3' || pKeyword === '!!3') {
      properties.priority = TaskPriorityOptions.Low;
    }
  }

  // Find the last valid time (either range or single) to determine start time and duration.
  const atKeyword = {
    [Locales.EnUS]: 'at',
    [Locales.NlNL]: 'om',
    [Locales.EsES]: 'a l(?:a|as)',
    [Locales.DeDE]: 'um',
  }[language];
  const atTimePatternString = `\\b${atKeyword}\\s+(${NLP.timePattern})\\b`;

  // Combine all time-related patterns into one, prioritizing the range.
  const combinedTimeRegex = new RegExp(
    `(${NLP.timeRangePatternString})|(${atTimePatternString})|(${NLP.singleTimePatternString})`,
    'gi',
  );

  const allTimeMatches = [...textToProcess.matchAll(combinedTimeRegex)].filter(
    (m) => !isIgnored(m) && !isInsideAGoal(m) && !isInsideATag(m),
  );

  const lastTimeMatch = allTimeMatches.pop();

  if (lastTimeMatch) {
    // Group 1 indicates a time range was matched
    if (lastTimeMatch[1]) {
      const rawStartStr = lastTimeMatch[2];
      const rawEndStr = lastTimeMatch[3];

      const start = parseTime(rawStartStr);
      const end = parseTime(rawEndStr);

      if (start && end) {
        const startHasPeriod = /am|pm/i.test(rawStartStr);
        const endHasPeriod = /am|pm/i.test(rawEndStr);

        // Infer AM/PM for the start time if it's ambiguous (e.g., "6:15-6:30pm")
        if (!startHasPeriod && endHasPeriod) {
          const endIsPm = /pm/i.test(rawEndStr);

          const startHour12 = start.hours % 12 === 0 ? 12 : start.hours % 12;
          const endHour12 = end.hours % 12 === 0 ? 12 : end.hours % 12;

          // If end time is PM and start hour is logically before it (e.g., 6 is before 7),
          // then the start time must also be PM.
          if (endIsPm && start.hours < 12 && startHour12 <= endHour12) {
            start.hours += 12;
            start.inMilliseconds += 12 * 60 * 60 * 1000;
            start.original = `${start.original}pm`; // Update the display string
          }
        }

        // Final validation to ensure the range is valid before setting properties
        if (end.hours * 60 + end.minutes > start.hours * 60 + start.minutes) {
          properties.startTime = start.inMilliseconds;
          properties.duration = end.inMilliseconds - start.inMilliseconds;
        }
      }
      // Group 4 indicates an 'at' time: e.g., "at 5pm"
    } else if (lastTimeMatch[4]) {
      const time = parseTime(lastTimeMatch[5]);
      if (time) {
        properties.startTime = time.inMilliseconds;
        properties.duration = null;
      }
      // Group 6 indicates a single time: e.g., "5pm"
    } else if (lastTimeMatch[6]) {
      const time = parseTime(lastTimeMatch[7]);
      if (time) {
        properties.startTime = time.inMilliseconds;
        // Explicitly set duration to null when a single time is the winner
        properties.duration = null;
      }
    }

    if (
      isNumber(properties.startTime) &&
      !properties.dueDate &&
      !properties.rrule
    ) {
      const now = new Date();
      const potentialDueDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0,
        properties.startTime,
      );

      // Select the correct keyword based on the current language.
      const keyword =
        potentialDueDate < now
          ? {
              [Locales.EnUS]: 'tomorrow',
              [Locales.NlNL]: 'morgen',
              [Locales.EsES]: 'mañana',
              [Locales.DeDE]: 'morgen',
            }[language]
          : {
              [Locales.EnUS]: 'today',
              [Locales.NlNL]: 'vandaag',
              [Locales.EsES]: 'hoy',
              [Locales.DeDE]: 'heute',
            }[language];

      const dateInfo = getDisplayDate(keyword, language, dateFormat);

      // Add a safety check to prevent a crash if dateInfo is null for any reason.
      if (dateInfo) {
        properties.dueDate = dateInfo.date;
      }
    }
  }

  // A separate duration keyword (e.g. "2h") can still be set, but only if a time range wasn't the winner.
  if (!isNumber(properties.duration)) {
    const durationRegex = /\b(?:\d+h(?:\s*\d+m)?|\d+m)\b/gi;
    const lastDurationMatch = [...textToProcess.matchAll(durationRegex)]
      .filter((m) => !isIgnored(m) && !isInsideAGoal(m) && !isInsideATag(m))
      .pop();

    if (lastDurationMatch) {
      let totalMinutes = 0;
      const hourMatch = lastDurationMatch[0].match(/(\d+)h/i);
      const minuteMatch = lastDurationMatch[0].match(/(\d+)m/i);
      if (hourMatch) {
        totalMinutes += parseInt(hourMatch[1], 10) * 60;
      }
      if (minuteMatch) {
        totalMinutes += parseInt(minuteMatch[1], 10);
      }
      if (totalMinutes) {
        properties.duration = totalMinutes * 60 * 1000;
      }
    }
  }

  const reminderRegex = new RegExp(NLP.reminderPatternString, 'gi');
  const lastReminderMatch = [...textToProcess.matchAll(reminderRegex)]
    .filter((m) => !isIgnored(m) && !isInsideAGoal(m) && !isInsideATag(m))
    .pop();

  if (lastReminderMatch) {
    const time = parseTime(lastReminderMatch[1]);
    if (time) {
      properties.reminderTime = time.inMilliseconds;
    }
  }

  // If a recurrence rule is set but no explicit start date, infer the first occurrence.
  if (properties.rrule && !properties.dueDate) {
    // 1. Parse the RRULE string once into a structured object for easier access.
    const rruleParts = properties.rrule
      .split(';')
      .reduce<Record<string, string>>((acc, part) => {
        const [key, value] = part.split('=');
        if (key && value) {
          acc[key.trim()] = value.trim();
        }
        return acc;
      }, {});

    const freq = rruleParts.FREQ;
    const interval = rruleParts.INTERVAL
      ? parseInt(rruleParts.INTERVAL, 10)
      : 1;
    const byDay = rruleParts.BYDAY;
    const byMonth = rruleParts.BYMONTH
      ? parseInt(rruleParts.BYMONTH, 10)
      : null;
    const byMonthDay = rruleParts.BYMONTHDAY
      ? rruleParts.BYMONTHDAY.split(',').map((d) => parseInt(d, 10))
      : null;
    const bySetPos = rruleParts.BYSETPOS
      ? rruleParts.BYSETPOS.split(',').map((p) => parseInt(p, 10))
      : null;

    let nextDate = null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayMapNumeric: Record<string, number> = {
      SU: 0,
      MO: 1,
      TU: 2,
      WE: 3,
      TH: 4,
      FR: 5,
      SA: 6,
    };

    const findDatesInPeriod = (
      year: number,
      startMonth: number,
      endMonth: number,
      targetDays: string[],
    ) => {
      const dates = [];
      const targetDayIndexes = targetDays.map((d) => dayMapNumeric[d]);
      for (let m = startMonth; m <= endMonth; m++) {
        const daysInMonth = new Date(year, m + 1, 0).getDate();
        for (let d = 1; d <= daysInMonth; d++) {
          const currentDate = new Date(year, m, d);
          if (targetDayIndexes.includes(currentDate.getDay())) {
            dates.push(currentDate);
          }
        }
      }
      return dates;
    };

    // --- Logic for FREQ=YEARLY ---
    if (freq === 'YEARLY') {
      let yearOffset = 0;
      while (!nextDate && yearOffset < 50) {
        const searchYear = now.getFullYear() + yearOffset;
        let baseYear = now.getFullYear();
        const firstEventThisYear = byMonth
          ? new Date(baseYear, byMonth - 1, byMonthDay ? byMonthDay[0] : 1)
          : new Date(baseYear, 0, 1);

        if (firstEventThisYear < today) {
          const daysInYear = byDay
            ? byDay.match(/[A-Z]{2}/g) || []
            : Object.keys(dayMapNumeric).map((k) => NLP.dayMap[language][k]);
          const allEventsThisYear = findDatesInPeriod(
            baseYear,
            byMonth ? byMonth - 1 : 0,
            byMonth ? byMonth - 1 : 11,
            daysInYear,
          );
          if (!allEventsThisYear.some((d) => d >= today)) {
            baseYear++;
          }
        }

        if ((searchYear - baseYear) % interval !== 0) {
          yearOffset++;
          continue;
        }

        if (byMonth && byMonthDay) {
          const potentialDate = new Date(
            searchYear,
            byMonth - 1,
            byMonthDay[0],
          );
          if (
            potentialDate >= today &&
            potentialDate.getMonth() === byMonth - 1
          ) {
            nextDate = potentialDate;
          }
        } else if (byDay) {
          // --- START OF FIX ---
          const byDayMatch = byDay.match(/([+-]?\d+)([A-Z]{2})/);
          const targetDays = byDay.split(',');
          let foundDate = null;

          // Loop through months of the current search year to find the first valid date
          const startMonth = byMonth ? byMonth - 1 : 0;
          const endMonth = byMonth ? byMonth - 1 : 11;

          for (let m = startMonth; m <= endMonth; m++) {
            let dateInMonth = null;

            if (bySetPos) {
              // Handles rules like "last weekday" -> BYDAY=MO,TU..., BYSETPOS=-1
              const pos = bySetPos[0];
              const candidateDatesInMonth = findDatesInPeriod(
                searchYear,
                m,
                m,
                targetDays,
              );
              if (pos > 0 && candidateDatesInMonth.length >= pos) {
                dateInMonth = candidateDatesInMonth[pos - 1];
              } else if (pos < 0 && candidateDatesInMonth.length >= -pos) {
                dateInMonth =
                  candidateDatesInMonth[candidateDatesInMonth.length + pos];
              }
            } else if (byDayMatch) {
              // Handles rules like "first monday" -> BYDAY=+1MO
              const position = parseInt(byDayMatch[1], 10);
              const singleTargetDay = [byDayMatch[2]]; // Safely use the matched day code (e.g., 'MO')
              const singleDayCandidates = findDatesInPeriod(
                searchYear,
                m,
                m,
                singleTargetDay,
              );
              if (position > 0 && singleDayCandidates.length >= position) {
                dateInMonth = singleDayCandidates[position - 1];
              } else if (
                position < 0 &&
                singleDayCandidates.length >= -position
              ) {
                dateInMonth =
                  singleDayCandidates[singleDayCandidates.length + position];
              }
            }

            if (dateInMonth && dateInMonth >= today) {
              foundDate = dateInMonth;
              break; // Found the earliest valid date, so exit the month loop
            }
          }

          if (foundDate) {
            nextDate = foundDate;
          }
        }
        yearOffset++;
      }
    }

    // --- Logic for FREQ=MONTHLY ---
    else if (freq === 'MONTHLY') {
      let monthOffset = 0;
      while (!nextDate && monthOffset < 50) {
        const searchDate = new Date(
          now.getFullYear(),
          now.getMonth() + monthOffset,
          1,
        );
        const searchYear = searchDate.getFullYear();
        const searchMonth = searchDate.getMonth();

        const monthDiff =
          (searchYear - now.getFullYear()) * 12 +
          (searchMonth - now.getMonth());
        if (monthDiff > 0 && monthDiff % interval !== 0) {
          monthOffset++;
          continue;
        }

        if (byMonthDay) {
          // Handles "every 15th"
          const potentialDate = new Date(
            searchYear,
            searchMonth,
            byMonthDay[0],
          );
          if (
            potentialDate.getMonth() === searchMonth &&
            potentialDate >= today
          ) {
            nextDate = potentialDate;
          }
        } else if (byDay) {
          // Handles "+1MO" and "MO,TU,WE..." with BYSETPOS
          const targetDays = byDay.split(',');
          const candidateDates = findDatesInPeriod(
            searchYear,
            searchMonth,
            searchMonth,
            targetDays,
          );
          let foundDate = null;

          if (bySetPos) {
            // Handles "first weekend" -> BYDAY=SA,SU; BYSETPOS=1
            const pos = bySetPos[0];
            if (pos > 0 && candidateDates.length >= pos) {
              foundDate = candidateDates[pos - 1];
            } else if (pos < 0 && candidateDates.length >= -pos) {
              foundDate = candidateDates[candidateDates.length + pos];
            }
          } else {
            // Handles "first monday" -> BYDAY=+1MO
            const byDayMatch = targetDays[0].match(/([+-]?\d+)([A-Z]{2})/);
            if (byDayMatch) {
              const position = parseInt(byDayMatch[1], 10);
              const singleTargetDay = [byDayMatch[2]];
              const singleDayCandidates = findDatesInPeriod(
                searchYear,
                searchMonth,
                searchMonth,
                singleTargetDay,
              );
              if (position > 0 && singleDayCandidates.length >= position) {
                foundDate = singleDayCandidates[position - 1];
              } else if (
                position < 0 &&
                singleDayCandidates.length >= -position
              ) {
                foundDate =
                  singleDayCandidates[singleDayCandidates.length + position];
              }
            }
          }

          if (foundDate && foundDate >= today) {
            nextDate = foundDate;
          }
        }
        monthOffset++;
      }
    }

    // --- Logic for FREQ=WEEKLY ---
    else if (freq === 'WEEKLY' && byDay) {
      const targetDayIndexes = byDay.split(',').map((d) => dayMapNumeric[d]);
      for (let i = 0; i < 366; i++) {
        const potentialDate = new Date(today);
        potentialDate.setDate(today.getDate() + i);
        if (targetDayIndexes.includes(potentialDate.getDay())) {
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          const diffWeeks = Math.floor(
            (potentialDate.getTime() - startOfWeek.getTime()) /
              (1000 * 60 * 60 * 24 * 7),
          );
          if (diffWeeks % interval === 0) {
            nextDate = potentialDate;
            break;
          }
        }
      }
    }

    // --- Logic for FREQ=DAILY ---
    else if (freq === 'DAILY') {
      nextDate = new Date(today); // Today is the first valid date.
    }

    if (nextDate) {
      properties.dueDate = nextDate;
    }
  }
  return properties;
};
